import { all, call, fork, put, takeEvery, take } from "redux-saga/effects";

import {
  getListReferralSourcesSuccess,
  getListReferralSourcesError,
  createReferralSourceSuccess,
  createReferralSourceError,
  updateReferralSourceSuccess,
  updateReferralSourceError,
  getReferralSourceSuccess,
  getReferralSourceError,
  getCoursesForOptionForReferralSourceSuccess,
  getCoursesForOptionForReferralSourceError,
} from "./actions";
import {
  GET_LIST_REFERRAL_SOURCE,
  CREATE_REFERRAL_SOURCE,
  UPDATE_REFERRAL_SOURCE,
  GET_REFERRAL_SOURCE,
  GET_COURSES_FOR_OPTION_FOR_REFERRAL_SOURCE,
} from "./actionTypes";
import { MAX_COURSES_LIMIT } from "../../../constants/courses";
import CourseService from '../../../services/CourseService';
import ReferralSourcesService from '../../../services/ReferralSourceService';
import { uploadFailure, uploadInitiate, uploadProgress, uploadSuccess } from '../../progress/actions';
import FilesService from '../../../services/FilesService';
import { END, eventChannel } from 'redux-saga';
import throwCustomError from '../../../helpers/throwCustomError';
import errorCodes from '../../../constants/errorCodes';

const getReferralSourceListAsync = async (data) => {
  const { model } = data;
  return await ReferralSourcesService.getList(model);
};

const createReferralSourceAsync = async (model) => {
  return await ReferralSourcesService.create(model);
};

const updateReferralSourceAsync = async ({ referralSourceId, model}) => {
  return await ReferralSourcesService.update(referralSourceId, model);
};

const getReferralSourceAsync = async (id) => {
  return await ReferralSourcesService.getOne(id);
};

// const removeBundleAsync = async (id) => {
//   return await BundleService.remove(id);
// };

// const changeBundleStatusAsync = async (id) => {
//   return await BundleService.changeStatus(id);
// };

const getCoursesForOptionListAsync = async (coursesCount = 0, coursesList = []) => {
  const filters = {
    onlyFreeCourse: true,
    onlyReferralSourceCourse: true,
  }

  const { pagination: { totalCount }, data } = await CourseService.getAllCoursesListWithLessonSummaryInfo({
    limit: MAX_COURSES_LIMIT,
    offset: coursesCount,
  }, filters);
  coursesList = [...coursesList, ...data.map(({ id, title }) => ({ id, title }))]

  if (totalCount > coursesList.length) {
    coursesList = await getCoursesForOptionListAsync(coursesList.length, coursesList)
  }

  return coursesList;
};

const referralSourceComposite = (model) => {
  const {
    title,
    code,
    courseIds,
    referralSourceThumbnailImage,
    heading,
    description,
  } = model;

  return {
    referralSourceThumbnailImage,
    referralSourceData: {
      title,
      code,
      courseIds,
      heading,
      description,
    },
  };
};

const getFilesArray = (referralSourceThumbnailImage) => {
  if (!!referralSourceThumbnailImage && !!referralSourceThumbnailImage.length) {
    return referralSourceThumbnailImage;
  }
};

const getFilesTotalSize = (referralSourceThumbnailImage) => {
  if (!!referralSourceThumbnailImage && !!referralSourceThumbnailImage.length) {
    return referralSourceThumbnailImage[0].size;
  } 
};

function* getReferralSourceList({ payload }) {
  try {
    const response = yield call(getReferralSourceListAsync, payload);
    yield put(getListReferralSourcesSuccess(response));
  } catch (error) {
    yield put(getListReferralSourcesError(error));
  }
}

function* getReferralSource({ payload: { referralSourceId } }) {
  try {
    const response = yield call(getReferralSourceAsync, referralSourceId);
    yield put(getReferralSourceSuccess(response));
  } catch (error) {
    yield put(getReferralSourceError(error));
  }
}

// function* changeBundleStatus({ payload: { id } }) {
//   try {
//     const response = yield call(changeBundleStatusAsync, id);
//     yield put(changeBundleStatusSuccess(response));
//   } catch (error) {
//     yield put(changeBundleStatusError(error));
//   }
// }

/**
 * Referral Source files channel for files uploading watching
 * @param data<Object>
 * @param mode<"create"|"update"> - mode of files progress channel (create by default)
 */
const referralSourceFilesChannel = (data, mode = "create") => {
  return eventChannel((emit) => {
    const { model } = data;
    const { referralSourceData, referralSourceThumbnailImage } = referralSourceComposite(model);
    const id = mode === "create" ? null : data.referralSourceId;
    const actionFunc =
      mode === "create"
        ? ReferralSourcesService.create(referralSourceData)
        : ReferralSourcesService.update(id, referralSourceData);

    if (
      (!referralSourceThumbnailImage || !referralSourceThumbnailImage.length)
    ) {
      // here is only one file and it's mandatory
      actionFunc
        .then(async (result) => {
          const referralSource = result.data;

          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    } else {
      const onProgress = ({ loaded }, file, chunkIndex) => {
        emit({ file, loaded, chunkIndex });
      };
      let referralSource;
      actionFunc
        .then(async ({ data }) => {
          referralSource = data;

          emit({
            files: getFilesArray(referralSourceThumbnailImage) || [],
            filesTotalSize: getFilesTotalSize(referralSourceThumbnailImage) || 0,
          });


          const referralSourceThumbnailImageResponse =
            referralSourceThumbnailImage &&
            referralSourceThumbnailImage.length &&
            (await FilesService.uploadImageFile({ files: referralSourceThumbnailImage }, onProgress).catch((error) => {
              throwCustomError(error);
            }));

          return { referralSourceThumbnailImageResponse };
        })
        .then(async ({ referralSourceThumbnailImageResponse }) => {
          if (!!referralSourceThumbnailImageResponse) {
            return ReferralSourcesService.addReferralSourceThumbnailImage(referralSource.id, {
              fileId: referralSourceThumbnailImageResponse[0].file.id,
            }).catch((error) => {
              throwCustomError(error);
            });
          }
         
          
        })
        .then((result) => {
          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    }
  });
};

function* createReferralSourceProgressListener(chan, history) {
  while (true) {
     const { files, filesTotalSize, file, loaded, chunkIndex, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }

     if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(createReferralSourceError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(createReferralSourceError(err));
        history.push(`/referral-sources`);
      }
      return;
    }

    if (success) {
      yield put(uploadSuccess(success));
      yield put(createReferralSourceSuccess(success));
      history.push(`/referral-sources`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded, chunkIndex }));
    }
  }
}

function* createReferralSource({ payload: { data, history } }) {
  try {

    yield fork(
      createReferralSourceProgressListener,
      referralSourceFilesChannel(data),
      history
    );
    // const response = yield call(createReferralSourceAsync, data);
    // yield put(createReferralSourceSuccess(response));
    // history.push(`/referral-sources`);
    // ToastrService.success(COUPON_CREATED);
  } catch (error) {
    yield put(createReferralSourceError(error));
  }
}

function* updateReferralSourceProgressListener(chan, history) {
  while (true) {
    const { files, filesTotalSize, file, loaded, chunkIndex, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }
    if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(updateReferralSourceError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(updateReferralSourceError(err));
        history.push(`/referral-sources`);
      }
      return;
    }
    if (success) {
      yield put(uploadSuccess(success));
      yield put(updateReferralSourceSuccess(success));
      history.push(`/referral-sources`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded, chunkIndex }));
    }
  }
}

function* updateReferralSource({ payload: { data, history } }) {
  try {
    yield fork(
          updateReferralSourceProgressListener,
          referralSourceFilesChannel(data, "update"),
          history
        );
    // ToastrService.success("Subscription coupon has been successfully updated");
  } catch (error) {
    yield put(updateReferralSourceError(error));
  }
}

// function* removeBundle({ payload: { data } }) {
//   try {
//     const response = yield call(removeBundleAsync, data);
//     yield put(removeBundleSuccess(response));
//   } catch (error) {
//     yield put(removeBundleError(error));
//   }
// }

function* getCoursesForOptionForReferralSource({payload: { limit = 0, offset = 0 }}) {
  try {
    const response = yield call(getCoursesForOptionListAsync);
    yield put(getCoursesForOptionForReferralSourceSuccess(response));
  } catch (error) {
    yield put(getCoursesForOptionForReferralSourceError(error))
  }
}

export function* watchGetReferralSourceList() {
  yield takeEvery(GET_LIST_REFERRAL_SOURCE, getReferralSourceList);
}

export function* watchCreateReferralSource() {
  yield takeEvery(CREATE_REFERRAL_SOURCE, createReferralSource);
}

export function* watchUpdateReferralSource() {
  yield takeEvery(UPDATE_REFERRAL_SOURCE, updateReferralSource);
}

// export function* watchRemoveBundle() {
//   yield takeEvery(REMOVE_BUNDLE, removeBundle);
// }

export function* watchGetReferralSource() {
  yield takeEvery(GET_REFERRAL_SOURCE, getReferralSource);
}

// export function* watchChangeBundleStatus() {
//   yield takeEvery(CHANGE_BUNDLE_STATUS, changeBundleStatus);
// }

export function* watchGetCoursesForOptionForReferralSource() {
  yield takeEvery(GET_COURSES_FOR_OPTION_FOR_REFERRAL_SOURCE, getCoursesForOptionForReferralSource)
}

function* coursesSaga() {
  yield all([
    fork(watchGetReferralSourceList),
    fork(watchCreateReferralSource),
    fork(watchUpdateReferralSource),
    // fork(watchRemoveBundle),
    fork(watchGetReferralSource),
    // fork(watchChangeBundleStatus),
    fork(watchGetCoursesForOptionForReferralSource)
  ]);
}

export default coursesSaga;
